export const PRICE_OPTIONS_URL = "https://beancount.io/prices/options.json";
const PRICE_URL_TEMPLATE = "https://beancount.io/prices/{base}-{quote}";
export const MAX_PRICE_SOURCES = 16;
export const DEFAULT_QUOTE = "USD";
export const QUOTE_PREFERENCE = "beancount.live-prices.quote.v1";

export const FIAT_CODES: ReadonlySet<string> = new Set([
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "CNY",
  "CAD",
  "AUD",
  "CHF",
  "HKD",
  "SGD",
  "NZD",
  "SEK",
  "NOK",
  "DKK",
  "PLN",
  "CZK",
  "HUF",
  "INR",
  "KRW",
  "TWD",
  "THB",
  "MYR",
  "IDR",
  "PHP",
  "VND",
  "BRL",
  "MXN",
  "ARS",
  "CLP",
  "COP",
  "ZAR",
  "TRY",
  "ILS",
  "AED",
  "SAR",
  "RUB",
  "UAH",
  "BGN",
  "RON",
  "ISK",
]);

export type PriceAsset = {
  id: string;
  code: string;
  kind: "crypto" | "equity" | "fiat";
  quotes: Set<string>;
};

export type PriceCatalog = {
  quotes: string[];
  assets: PriceAsset[];
  byId: Map<string, PriceAsset>;
  byAlias: Map<string, PriceAsset>;
  delisted: Set<string>;
};

type PriceOptions = {
  url_template: string;
  quotes: string[];
  quote_sets: string[][];
  groups: {
    class: PriceAsset["kind"];
    quote_set: number;
    bases: string[];
  }[];
  delisted: string[];
};

const CODE = /^[A-Z][A-Z0-9._-]{0,31}$/;
const codes = (value: unknown): value is string[] =>
  Array.isArray(value) &&
  value.every((code) => typeof code === "string" && CODE.test(code));

/** Decode the compact catalog once. A symbol alone is not an asset identity. */
export function parsePriceCatalog(input: unknown): PriceCatalog {
  const data = input as PriceOptions | null;
  if (
    !data ||
    data.url_template !== PRICE_URL_TEMPLATE ||
    !codes(data.quotes) ||
    data.quotes.length === 0 ||
    !Array.isArray(data.quote_sets) ||
    !data.quote_sets.every(codes) ||
    !Array.isArray(data.groups) ||
    data.groups.length === 0 ||
    !Array.isArray(data.delisted) ||
    !data.delisted.every((alias) => typeof alias === "string")
  )
    throw new Error("Invalid price catalog");

  const quoteCodes = new Set(data.quotes);
  const delisted = new Set(data.delisted);
  const byId = new Map<string, PriceAsset>();
  const byAlias = new Map<string, PriceAsset>();
  for (const group of data.groups) {
    if (
      !group ||
      !["crypto", "equity", "fiat"].includes(group.class) ||
      !codes(group.bases) ||
      !Number.isInteger(group.quote_set) ||
      !data.quote_sets[group.quote_set]
    )
      throw new Error("Invalid price catalog group");
    const quotes = data.quote_sets[group.quote_set];
    if (quotes.some((quote) => !quoteCodes.has(quote)))
      throw new Error("Unknown catalog quote currency");

    for (const code of group.bases) {
      // Fiat traded on a crypto venue is still fiat; retain real stock/token collisions.
      const kind =
        FIAT_CODES.has(code) && group.class === "crypto" ? "fiat" : group.class;
      const id = `${kind}:${code}`;
      const asset = byId.get(id) ?? {
        id,
        code,
        kind,
        quotes: new Set<string>(),
      };
      for (const quote of quotes) {
        const alias = `${code}-${quote}`;
        if (code === quote || delisted.has(alias)) continue;
        if (alias.length > 64) throw new Error("Invalid price alias");
        const previous = byAlias.get(alias);
        if (previous && previous.id !== id)
          throw new Error("Ambiguous price alias");
        asset.quotes.add(quote);
        byAlias.set(alias, asset);
      }
      if (asset.quotes.size > 0) byId.set(id, asset);
    }
  }
  if (byAlias.size === 0) throw new Error("Empty price catalog");
  const assets = [...byId.values()].sort(
    (a, b) => a.code.localeCompare(b.code) || a.kind.localeCompare(b.kind),
  );
  return { quotes: [...quoteCodes].sort(), assets, byId, byAlias, delisted };
}

export function priceInclude(base: string, quote: string): string {
  return `include "${PRICE_URL_TEMPLATE.replace("{base}", base).replace("{quote}", quote)}"`;
}

/** Editorial shortcuts, not a popularity ranking or a separate coverage list. */
const QUICK_PICKS = [
  "crypto:BTC",
  "crypto:ETH",
  "crypto:SOL",
  "equity:AAPL",
  "equity:NVDA",
  "equity:SPY",
  "equity:VTI",
  "equity:VOO",
];

export function quickPriceAssets(
  catalog: PriceCatalog,
  quote: string,
): PriceAsset[] {
  return QUICK_PICKS.flatMap((id) => {
    const asset = catalog.byId.get(id);
    return asset?.quotes.has(quote) ? [asset] : [];
  });
}

export function searchPriceAssets(
  catalog: PriceCatalog,
  quote: string,
  query: string,
): PriceAsset[] {
  const normalized = query.trim().toUpperCase();
  const matches = catalog.assets.filter(
    (asset) => asset.quotes.has(quote) && asset.code.includes(normalized),
  );
  // Exact and prefix matches precede infix matches; cap DOM work for the 12k-symbol catalog.
  const rank = (code: string) =>
    code === normalized ? 0 : code.startsWith(normalized) ? 1 : 2;
  return matches.sort((a, b) => rank(a.code) - rank(b.code)).slice(0, 8);
}

/** Never substitute a same-symbol asset when its selected quote is unavailable. */
export function selectedPriceIncludes(
  assets: readonly PriceAsset[],
  quote: string,
): string {
  return assets
    .filter((asset) => asset.quotes.has(quote))
    .map((asset) => priceInclude(asset.code, quote))
    .join("\n");
}

const REGION_QUOTES: Record<string, string> = {
  US: "USD",
  GB: "GBP",
  CN: "CNY",
  HK: "HKD",
  TW: "TWD",
  SG: "SGD",
  JP: "JPY",
  KR: "KRW",
  CA: "CAD",
  AU: "AUD",
  NZ: "NZD",
  CH: "CHF",
  LI: "CHF",
  IN: "INR",
  BR: "BRL",
  MX: "MXN",
  ZA: "ZAR",
  SE: "SEK",
  NO: "NOK",
  DK: "DKK",
  PL: "PLN",
  CZ: "CZK",
  HU: "HUF",
  RO: "RON",
  IS: "ISK",
  ID: "IDR",
  MY: "MYR",
  PH: "PHP",
  TH: "THB",
  TR: "TRY",
  IL: "ILS",
  RU: "RUB",
  UA: "UAH",
  IR: "IRR",
};
const EURO_REGIONS = new Set([
  "AT",
  "BE",
  "BG",
  "CY",
  "DE",
  "EE",
  "ES",
  "FI",
  "FR",
  "GR",
  "HR",
  "IE",
  "IT",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "PT",
  "SI",
  "SK",
]);
const LANGUAGE_QUOTES: Record<string, string> = {
  en: "USD",
  zh: "CNY",
  ja: "JPY",
  ko: "KRW",
  de: "EUR",
  fr: "EUR",
  it: "EUR",
  es: "EUR",
  ca: "EUR",
  nl: "EUR",
  pt: "EUR",
  sk: "EUR",
  bg: "EUR",
  ru: "RUB",
  uk: "UAH",
};

/** Explicit choices win; the page locale takes priority over browser hints. */
export function preferredPriceQuote(
  quotes: readonly string[],
  locale: string,
  browserLocales: readonly string[] = [],
  saved?: string | null,
): string {
  if (saved && quotes.includes(saved)) return saved;
  const fallback = quotes.includes(DEFAULT_QUOTE) ? DEFAULT_QUOTE : quotes[0];
  for (const tag of [locale, ...browserLocales]) {
    try {
      const { region, language } = new Intl.Locale(tag);
      const quote =
        (region &&
          (EURO_REGIONS.has(region) ? "EUR" : REGION_QUOTES[region])) ||
        LANGUAGE_QUOTES[language];
      if (quote) return quotes.includes(quote) ? quote : fallback;
    } catch {
      /* Ignore malformed browser locale hints. */
    }
  }
  return fallback;
}
